//useJobProgress.js

import {useEffect,useState} from "react";
import SockJS from "sockjs-client";
import {Client} from "@stomp/stompjs";
import {loadJobStatus} from "../api/reportApi";

const TERMINAL_STATUSES=["FAILED","ERROR","ANALYSIS_DONE","DONE","COMPLETED"];

export function useJobProgress(jobId){
    const [progress,setProgress]=useState(0);
    const [status,setStatus]=useState("WAITING");
    const [timestamp,setTimestamp]=useState(null);
    const [error,setError]=useState(null);
    const [errorCode,setErrorCode]=useState(null);

    useEffect(()=>{
        if(!jobId){
            setStatus("WAITING");
            setProgress(0);
            setTimestamp(null);
            setError(null);
            return;
        }

        setStatus("WAITING");
        setProgress(0);
        setTimestamp(null);
        setError(null);

        let client=null,pollInterval=null,destroyed=false;

        const stopPolling=()=>{
            if(pollInterval){
                clearInterval(pollInterval);
                pollInterval=null;
            }
        };

        const applyStatus=data=>{
            if(!data||destroyed)return;

            const currentStatus=(data.status||"WAITING").toUpperCase();

            if(currentStatus==="NOT_FOUND"){
                stopPolling();
                setStatus("NOT_FOUND");
                setProgress(0);
                setTimestamp(null);
                if(client?.active)client.deactivate();
                return;
            }

            setProgress(typeof data.progress==="number"?data.progress:0);
            setStatus(currentStatus);
            setTimestamp(data.timestamp||new Date().toISOString());
            setError(data.errorMessage||data.message||null);
            setErrorCode(data.errorCode||null);
        };

        const startPollingFallback=()=>{
            if(pollInterval)return;

            pollInterval=setInterval(async()=>{
                try{
                    const data=await loadJobStatus(jobId);
                    if(!data)return;

                    applyStatus(data);

                    if(TERMINAL_STATUSES.includes((data.status||"").toUpperCase()))
                        stopPolling();
                }catch(e){
                    console.warn("Polling failed",e);
                }
            },2000);
        };

        const init=async()=>{
            try{
                const initial=await loadJobStatus(jobId);

                if(initial){
                    applyStatus(initial);

                    if(TERMINAL_STATUSES.includes((initial.status||"").toUpperCase()))
                        return;
                }
            }catch(e){
                console.warn("Status preload failed",e);
            }

            const socket=new SockJS("/ws");

            client=new Client({
                webSocketFactory:()=>socket,
                reconnectDelay:5000,
                debug:()=>{}
            });

            client.onConnect=async()=>{
                client.subscribe(`/topic/jobs/${jobId}`,message=>{
                    try{
                        applyStatus(JSON.parse(message.body));
                    }catch(e){
                        console.warn("WS parse failed",e);
                    }
                });

                try{
                    const latest=await loadJobStatus(jobId);

                    if(latest){
                        applyStatus(latest);

                        if(TERMINAL_STATUSES.includes((latest.status||"").toUpperCase()))
                            client.deactivate();
                    }
                }catch(e){
                    console.warn("Latest status check failed",e);
                }
            };

            client.onStompError=startPollingFallback;
            client.onWebSocketClose=startPollingFallback;
            client.onWebSocketError=startPollingFallback;
            client.activate();
        };

        init();

        return()=>{
            destroyed=true;
            stopPolling();
            if(client?.active)client.deactivate();
        };
    },[jobId]);

    return {progress,status,timestamp,error,errorCode};
}